/**
 * Мост «страница ↔ service worker» (CLIENT_WEB.md §8.4, §9.5).
 *
 * Worker не умеет переходить по маршрутам приложения: он фокусирует вкладку
 * и присылает маршрут сообщением. Переход выполняется ТОЛЬКО после replay
 * (SYS-11) и по правилу «актуальное состояние» (§8.4 п. 6) — поэтому клик
 * ведёт на N-05, который сам дотягивает объект.
 */
import { onUpdateAvailable, registerServiceWorker, watchForUpdates } from '@shared/pwa/register'

import { useAppStore } from '../model/app-store'
import { router } from '../routes/router'

interface WorkerMessage {
  type?: string
  route?: string
  notification_id?: string | null
}

/** Маршрут, дождавшийся готовности приложения. */
let pendingRoute: string | null = null

export function startServiceWorkerBridge(): () => void {
  void registerServiceWorker()

  const stopUpdateWatch = watchForUpdates()
  const stopUpdateListener = onUpdateAvailable(() => {
    // SYS-04: предложение обновиться, без автоматической перезагрузки (§9.5 п. 2).
    useAppStore.getState().setUpdateAvailable(true)
  })

  const onMessage = (event: MessageEvent): void => {
    const data = event.data as WorkerMessage | undefined
    if (!data || data.type !== 'NAVIGATE' || !data.route) return

    // Приложение могло ещё не восстановить сессию — маршрут ждёт своего часа.
    if (!readyForNavigation()) {
      pendingRoute = data.route
      return
    }

    navigateTo(data.route)
  }

  navigator.serviceWorker?.addEventListener('message', onMessage)

  // Как только приложение стало готово — исполняем отложенный переход.
  const unsubscribe = useAppStore.subscribe((state, previous) => {
    if (state.phase === previous.phase && state.syncStatus === previous.syncStatus) return
    if (!pendingRoute || !readyForNavigation()) return

    const route = pendingRoute
    pendingRoute = null
    navigateTo(route)
  })

  return () => {
    navigator.serviceWorker?.removeEventListener('message', onMessage)
    unsubscribe()
    stopUpdateWatch()
    stopUpdateListener()
  }
}

/**
 * §8.4 п. 6: переход после replay. `live` и `polling_fallback` означают, что
 * догон событий завершён; в `polling_fallback` ждать больше нечего
 * (GAP-COMMON-01).
 */
function readyForNavigation(): boolean {
  const { phase, syncStatus } = useAppStore.getState()
  if (phase !== 'ready') return false
  return syncStatus === 'live' || syncStatus === 'polling_fallback' || syncStatus === 'offline'
}

function navigateTo(route: string): void {
  void router.navigate({ to: route, replace: false })
}
